import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, Plant, CurrencyCircleDollar, Basket, Handshake, ChartBar, Ruler } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EMPTY_CROP, HARVEST_UNIT_OPTIONS, today, fmt } from "./constants";


export function AreaCostAnalysis({ crops }) {
  // Sadece dekar girilmiş ürünleri analize dahil et
  const withArea = crops.filter(c => Number(c.area_dekar || 0) > 0);
  if (crops.length === 0) return null;

  if (withArea.length === 0) {
    return (
      <Card className="mt-6" data-testid="fields-area-analysis-empty">
        <div className="flex items-center gap-2 mb-2">
          <Ruler size={18} weight="duotone" className="text-[#DDA74F]" />
          <h3 className="heading text-lg">Alan Bazlı Maliyet Analizi</h3>
        </div>
        <div className="text-sm text-[#6b746c] bg-[#FFF7E6] border border-[#F0DAA8] px-3 py-2 rounded">
          Bu bölümde dekar başına maliyet, dekar başına verim ve birim üretim maliyeti hesaplanır.<br/>
          <b>Aktifleştirmek için</b>: "Genel" sekmesinden bir ürünü düzenleyip <b>Büyüklük (Dekar)</b> alanını doldurun.
        </div>
      </Card>
    );
  }

  // Global özet (dekar girilmiş ürünlerin toplamı)
  const totalArea = withArea.reduce((s, c) => s + Number(c.area_dekar || 0), 0);
  const totalExpense = withArea.reduce((s, c) => s + Number(c.total_expense || 0), 0);
  const totalHarvestValue = withArea.reduce((s, c) => s + Number(c.total_harvest_value || 0), 0);
  const avgCostPerDekar = totalArea > 0 ? totalExpense / totalArea : 0;

  return (
    <Card className="mt-6" data-testid="fields-area-analysis">
      <div className="flex items-center gap-2 mb-3">
        <ChartBar size={18} weight="duotone" className="text-[#DDA74F]" />
        <h3 className="heading text-lg">Alan Bazlı Maliyet Analizi</h3>
        <span className="text-xs text-[#8a938c]">— dekar bazında maliyet, verim ve birim üretim maliyeti</span>
      </div>

      {/* Global özet */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="fields-area-summary">
        <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-[#8a938c]">Toplam Alan</div>
          <div className="mt-1 text-2xl font-bold text-[#2D4A36] tabnum">{fmt(totalArea)}
            <span className="text-sm text-[#8a938c] font-normal ml-1">dekar</span>
          </div>
          <div className="text-[11px] text-[#8a938c] mt-0.5">{withArea.length} ürün</div>
        </div>
        <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-[#8a938c]">Toplam Gider</div>
          <div className="mt-1 text-2xl font-bold text-[#C45B45] tabnum">{fmt(totalExpense)}
            <span className="text-sm text-[#8a938c] font-normal ml-1">₺</span>
          </div>
        </div>
        <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-[#8a938c]">Ortalama Dekar Maliyeti</div>
          <div className="mt-1 text-2xl font-bold text-[#DDA74F] tabnum" data-testid="fields-area-avg-cost">{fmt(Math.round(avgCostPerDekar * 100) / 100)}
            <span className="text-sm text-[#8a938c] font-normal ml-1">₺/dekar</span>
          </div>
          <div className="text-[11px] text-[#8a938c] mt-0.5">giderin alana oranı</div>
        </div>
        <div className="border border-[#E5E2DC] rounded-md p-3 bg-white">
          <div className="text-xs uppercase tracking-wider text-[#8a938c]">Toplam Üretim Değeri</div>
          <div className="mt-1 text-2xl font-bold text-[#2D4A36] tabnum">{fmt(totalHarvestValue)}
            <span className="text-sm text-[#8a938c] font-normal ml-1">₺</span>
          </div>
          <div className="text-[11px] text-[#8a938c] mt-0.5">hasat × piyasa fiyatı</div>
        </div>
      </div>

      {/* Ürün başı analiz tablosu */}
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ürün</th>
              <th className="text-right">Alan</th>
              <th className="text-right">Toplam Gider</th>
              <th className="text-right">Dekar Başına Maliyet</th>
              <th className="text-right">Toplam Hasat</th>
              <th className="text-right">Dekar Başına Verim</th>
              <th className="text-right">Birim Üretim Maliyeti</th>
              <th className="text-right">Net (Değer − Gider)</th>
            </tr>
          </thead>
          <tbody>
            {withArea.map(c => {
              const unit = c.last_unit || "birim";
              const net = Number(c.net || 0);
              const netTone = net >= 0 ? "text-[#2D4A36]" : "text-[#C45B45]";
              return (
                <tr key={c.id} data-testid={`fields-area-row-${c.id}`}>
                  <td className="font-semibold">{c.name}</td>
                  <td className="text-right tabnum">{fmt(c.area_dekar)} <span className="text-xs text-[#8a938c]">dekar</span></td>
                  <td className="text-right tabnum text-[#C45B45] font-semibold">{fmt(c.total_expense)} ₺</td>
                  <td className="text-right tabnum">
                    {c.cost_per_dekar != null
                      ? <span className="text-[#DDA74F] font-semibold" data-testid={`fields-area-cpd-${c.id}`}>{fmt(c.cost_per_dekar)} ₺/dekar</span>
                      : <span className="text-xs text-[#8a938c]">—</span>}
                  </td>
                  <td className="text-right tabnum">{fmt(c.total_harvest_amount)} <span className="text-xs text-[#8a938c]">{unit}</span></td>
                  <td className="text-right tabnum">
                    {c.yield_per_dekar != null
                      ? (
                        <div>
                          <div className="font-semibold text-[#2D4A36]" data-testid={`fields-area-ypd-${c.id}`}>
                            {fmt(c.yield_per_dekar)} <span className="text-xs text-[#8a938c] font-normal">{unit}/dekar</span>
                          </div>
                          {c.yield_per_dekar_kg != null && unit !== "kg" && (
                            <div className="text-[11px] text-[#8a938c]">≈ {fmt(c.yield_per_dekar_kg)} kg/dekar</div>
                          )}
                        </div>
                      )
                      : <span className="text-xs text-[#8a938c]">Hasat girilmedi</span>}
                  </td>
                  <td className="text-right tabnum">
                    {c.cost_per_unit != null
                      ? (
                        <div>
                          <div className="font-semibold" data-testid={`fields-area-cpu-${c.id}`}>
                            {fmt(c.cost_per_unit)} <span className="text-xs text-[#8a938c] font-normal">₺/{unit}</span>
                          </div>
                          {c.cost_per_kg != null && unit !== "kg" && (
                            <div className="text-[11px] text-[#8a938c]">≈ {fmt(c.cost_per_kg)} ₺/kg</div>
                          )}
                        </div>
                      )
                      : <span className="text-xs text-[#8a938c]">Hasat girilmedi</span>}
                  </td>
                  <td className={`text-right tabnum font-semibold ${netTone}`}>{fmt(net)} ₺</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-[#5e675e] bg-[#F5F2EC] border border-[#E5E2DC] px-3 py-2 rounded space-y-1">
        <div>📊 <b>Dekar Başına Maliyet</b> = Toplam Gider ÷ Alan. Farklı ürünlerin verimliliğini karşılaştırmanıza yardımcı olur.</div>
        <div>🌾 <b>Dekar Başına Verim</b> = Toplam Hasat ÷ Alan. Silaj/yonca üreticileri için kritik metrik.</div>
        <div>💰 <b>Birim Üretim Maliyeti</b> = Toplam Gider ÷ Toplam Hasat. Kendi ürettiğiniz bir kg silajın size kaça mal olduğunu gösterir.</div>
      </div>
    </Card>
  );
}
